import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FichaLiquidacionService } from 'src/app/services/liquidacion/ficha-liquidacion.service';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { claseMarca, fechaLiq, puedeVerLiquidacion, TEXTO_MARCA } from 'src/app/services/liquidacion/marcas';

/**
 * FICHA RÁPIDA DE LA LIQUIDACIÓN (Mario, 12-oct; FIC-03): el panel lateral que abre cualquier LIQ de la consola.
 * Lo básico para resolver un caso sin salir de la pantalla: estado, fechas clave con quién y cuándo, montos, cuenta
 * por pagar, pagarés que entraron e historia. Lo demás, en «Abrir en Consultas». Solo con `liquidacion.consultar`.
 */
@Component({
  selector: 'app-ficha-liquidacion',
  templateUrl: './ficha-liquidacion.component.html',
})
export class FichaLiquidacionComponent implements OnInit, OnDestroy {
  /** Escape cierra la ficha aunque el foco no esté dentro del panel (en MT no cerraba, 14-oct). */
  @HostListener('document:keydown.escape') alEscape(): void { if (this.codigo) this.cerrar(); }
  codigo: string | null = null;
  f: any = null;
  cargando = false;
  error: string | null = null;
  private sub?: Subscription;
  /** La barra aparece anidada en alguna pantalla (planes): solo el primer panel se pinta. */
  private static activo: FichaLiquidacionComponent | null = null;
  get soyElActivo(): boolean { return FichaLiquidacionComponent.activo === this; }

  readonly ACCION: any = {
    PREPARAR: 'Preparó', INCLUIR: 'Revisada: incluir', ARMAR_LOTE: 'Armó el lote', GENERAR: 'Generó el archivo', DESCARGAR: 'Descargó el Excel',
    CAMBIAR_CUENTA_ORIGEN: 'Cambió la cuenta origen', MARCAR_EN_TANDA: 'Marcada en tanda', APROBAR: 'Aprobada', DEVOLVER: 'Devuelta',
    ENVIAR_AL_BANCO: 'Pagado en la banca', CARGAR_RESPUESTA: 'Respuesta del banco', REINTENTAR: 'Propuesta nueva', PASAR_A_MANANA: 'Pasada a mañana o retenida',
    ANULAR: 'Anulada', AJUSTAR: 'Ajuste', CERRAR: 'Cerrada',
  };

  constructor(private ficha: FichaLiquidacionService, private liq: LiquidacionService, private auth: AuthService) { }

  ngOnInit(): void {
    if (!FichaLiquidacionComponent.activo) FichaLiquidacionComponent.activo = this;
    this.sub = this.ficha.codigo$.subscribe((c) => { if (!this.soyElActivo) return; this.codigo = c; if (c) this.cargar(c); else this.f = null; });
  }
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (FichaLiquidacionComponent.activo === this) FichaLiquidacionComponent.activo = null;
  }

  get puede(): boolean { return puedeVerLiquidacion(this.auth); }

  cargar = async (code: string): Promise<void> => {
    this.f = null; this.error = null;
    if (!this.puede) { this.error = 'Para ver la liquidación hace falta el permiso «liquidacion.consultar».'; return; }
    this.cargando = true;
    try { const r = await this.liq.liquidacion(code); if (this.codigo === code) this.f = r; }
    catch (e: any) { if (this.codigo === code) this.error = e?.error?.msg || 'No se pudo leer la liquidación.'; }
    finally { this.cargando = false; }
  };

  cerrar = (): void => this.ficha.cerrar();

  /** El estado como se lee en la consola (EST-11: «Liquidado» solo con el pago confirmado). */
  get marca(): string {
    const f = this.f || {};
    if (f.estado === 'PAID' || f.payout_state === 'PAGADA') return 'PAGADO';
    if (f.payout_state === 'EN_LOTE') return 'EN_LOTE';
    if (f.payout_state === 'NO_PROCESADA') return 'NO_PROCESADO';
    return 'POR_PAGAR';
  }
  get textoMarca(): string { return TEXTO_MARCA[this.marca]; }
  claseMarca = claseMarca;
  fecha = (iso: string | null | undefined): string => fechaLiq(iso);
  /** Hora de Venezuela (UTC−4): «12/10/2026 15:47». */
  hora = (f: any): string => {
    if (!f) return '—';
    const d = new Date(new Date(f).getTime() - 4 * 3600e3);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  };
}
